import { SearchResultPageLink } from '@app/data/clients/rbServerSideClient/utils/buildShopPagination';
import {
  Box,
  Button,
  ChevronLeftBigIcon,
  ChevronLeftIcon,
  ChevronRightBigIcon,
  ChevronRightIcon,
  ContextHorizontalIcon,
  constants,
  Text,
} from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import { messages } from './messages';
import styles from './Pagination.module.scss';

interface PaginationProps {
  firstGap: boolean;
  middlePages: SearchResultPageLink[];
  lastGap: boolean;
  firstPage?: SearchResultPageLink;
  lastPage?: SearchResultPageLink;
  nextPage?: SearchResultPageLink;
  previousPage?: SearchResultPageLink;
  handlePagination: (url: string) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  firstGap,
  middlePages,
  lastGap,
  firstPage,
  lastPage,
  nextPage,
  previousPage,
  handlePagination,
}) => {
  const intl = useIntl();

  return (
    <Box
      className={styles.paginationContainer}
      aria-label={intl.formatMessage(messages.pagNav)}
    >
      {previousPage ? (
        <>
          <div className={[styles.previousButton, styles.mobile].join(' ')}>
            <Button
              onClick={() => handlePagination(previousPage.link)}
              iconBefore={<ChevronLeftBigIcon size={constants.MEDIUM} />}
              circle
              aria-label={intl.formatMessage(messages.previousPage)}
            />
          </div>
          <div className={[styles.previousButton, styles.desktop].join(' ')}>
            <ChevronLeftIcon size={constants.SMALL} />
            <Text
              className={styles.previousButtonText}
              onClick={() => handlePagination(previousPage.link)}
              aria-label={intl.formatMessage(messages.previousPage)}
              type="body2"
            >
              {intl.formatMessage(messages.previous)}
            </Text>
          </div>
        </>
      ) : (
        <div
          className={[
            styles.previousButton,
            styles.desktop,
            styles.disabled,
          ].join(' ')}
        >
          <ChevronLeftIcon size={constants.SMALL} />
          <Text className={styles.previousButtonText} type="body2">
            {intl.formatMessage(messages.previous)}
          </Text>
        </div>
      )}
      <div className={styles.buttonContainer}>
        {firstPage && (
          <button
            className={[
              styles.paginationButton,
              firstPage.active && styles.active,
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handlePagination(firstPage.link)}
            aria-current={firstPage.active ? 'true' : undefined}
            aria-label={intl.formatMessage(messages.goToPage, {
              pageNumber: firstPage.text,
            })}
            type="button"
          >
            <Text className={styles.paginationText}>{firstPage.text}</Text>
          </button>
        )}
        {firstGap && (
          <span
            className={styles.paginationGap}
            aria-hidden="true"
            role="presentation"
          >
            <ContextHorizontalIcon size={constants.SMALL} />
          </span>
        )}
        {middlePages.map((page) => (
          <button
            key={page.text}
            className={[styles.paginationButton, page.active && styles.active]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handlePagination(page.link)}
            aria-current={page.active ? 'true' : undefined}
            aria-label={intl.formatMessage(messages.goToPage, {
              pageNumber: page.text,
            })}
            type="button"
          >
            <Text className={styles.paginationText}>{page.text}</Text>
          </button>
        ))}
        {lastGap && (
          <span
            className={styles.paginationGap}
            aria-hidden="true"
            role="presentation"
          >
            <ContextHorizontalIcon size={constants.SMALL} />
          </span>
        )}
        {lastPage && (
          <button
            className={[
              styles.paginationButton,
              lastPage.active && styles.active,
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handlePagination(lastPage.link)}
            aria-current={lastPage.active ? 'page' : undefined}
            aria-label={intl.formatMessage(messages.goToPage, {
              pageNumber: lastPage.text,
            })}
            type="button"
          >
            <Text className={styles.paginationText}>{lastPage.text}</Text>
          </button>
        )}
      </div>
      {nextPage ? (
        <>
          <div className={[styles.nextButton, styles.mobile].join(' ')}>
            <Button
              onClick={() => handlePagination(nextPage.link)}
              iconBefore={<ChevronRightBigIcon size={constants.MEDIUM} />}
              circle
              aria-label={intl.formatMessage(messages.nextPage)}
            />
          </div>
          <div className={[styles.nextButton, styles.desktop].join(' ')}>
            <Text
              className={styles.nextButtonText}
              onClick={() => handlePagination(nextPage.link)}
              aria-label={intl.formatMessage(messages.nextPage)}
              type="body2"
            >
              {intl.formatMessage(messages.next)}
            </Text>
            <ChevronRightIcon size={constants.SMALL} />
          </div>
        </>
      ) : (
        <div
          className={[styles.nextButton, styles.desktop, styles.disabled].join(
            ' '
          )}
        >
          <Text className={styles.nextButtonText} type="body2">
            {intl.formatMessage(messages.next)}
          </Text>
          <ChevronRightIcon size={constants.SMALL} />
        </div>
      )}
    </Box>
  );
};
